import React, { useState, useEffect } from "react";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./css/MatchSetup.css";
import config from "./config";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { v4 as uuidv4 } from "uuid";

const devLog = (...args) => {
  if (config.IS_DEV) {
    console.log(...args);
  }
};

const API_URL =
  "https://lykns00a87.execute-api.us-east-2.amazonaws.com/default/football_app_data_crud"; // Replace with actual API URL

const MatchSetup = ({ onSetupComplete, userId: propUserId }) => {
  const [userId, setUserId] = useState(propUserId);
  //const [clubs, setClubs] = useState([]); // Holds list of clubs
  const [myTeam, setMyTeam] = useState(""); // Holds the selected team name
  //const [selectedClub, setSelectedClub] = useState(""); // Holds the selected club
  const [teams, setTeams] = useState([]); // Holds list of teams based on the selected club
  const [players, setPlayers] = useState([]); // Filtered players based on selected team
  const [selectedTeamId, setSelectedTeamId] = useState(""); // Holds the selected team ID
  const [selectedTeamName, setSelectedTeamName] = useState(""); // Holds the selected team name
  const [playerInput, setPlayerInput] = useState(""); // For manual player input
  const [opposition, setOpposition] = useState(""); // Holds the opposition
  const [homeAway, setHomeAway] = useState("home"); // Home/Away status
  const [otherInfo, setOtherInfo] = useState(""); // Holds additional match info
  const [loadingData, setLoading] = useState(false); // Loading indicator for both teams and players
  const [error, setError] = useState(""); // Error state
  const teamSelectRef = useRef(null); // Reference for the team select dropdown
  const navigate = useNavigate(); // Navigation hook
  const [playersVisible, setPlayersVisible] = useState(false); // Track players list visibility

  useEffect(() => {
    devLog("useEffect triggered: Checking for userId to fetch teams.");

    if (userId) {
      devLog(`userId found: ${userId}. Fetching teams...`);
      fetchTeams(userId);
    } else {
      devLog("userId not found. Skipping fetchTeams.");
    }
  }, [userId]);

  const fetchTeamName = async (teamId) => {
    try {
      const requestPayload = {
        operationType: "get_team_name",
        data: { team_id: teamId },
      };

      const response = await axios.post(`${API_URL}`, requestPayload);
      const responseData = response.data.body
        ? JSON.parse(response.data.body)
        : {};

      devLog(`Response for team ${teamId}:`, responseData);

      return responseData.team_name || "Unknown Team";
    } catch (error) {
      console.error(`Error fetching team name for team ${teamId}:`, error);
      return "Unknown Team";
    }
  };

  const fetchTeams = async (managerId) => {
    devLog("Fetching teams for managerId:", managerId);
    setLoading(true);

    try {
      const requestPayload = {
        operationType: "fetch_teams_by_manager_id",
        data: { manager_id: managerId },
      };

      const response = await axios.post(`${API_URL}`, requestPayload);
      const teamsData = response.data.body
        ? JSON.parse(response.data.body).teams
        : [];
      devLog("Fetched team IDs:", teamsData);

      const detailedTeams = await Promise.all(
        teamsData.map(async (team) => {
          const team_name = await fetchTeamName(team.team_id);
          devLog(`Fetched team name for ${team.team_id}: ${team_name}`);
          return { team_id: team.team_id, team_name };
        })
      );

      devLog("Detailed teams with names:", detailedTeams);
      setTeams(detailedTeams);

      // Automatically select the team if there's only one
      if (detailedTeams.length === 1) {
        const singleTeam = detailedTeams[0];
        setSelectedTeamId(singleTeam.team_id);
        setSelectedTeamName(singleTeam.team_name);
        setMyTeam(singleTeam.team_name); // Set myTeam if there's only one team
        fetchPlayers(singleTeam.team_id);
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
      setError("Failed to load teams. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async (teamId) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}`, {
        operationType: "fetch_detailed_players_by_team",
        data: { team_id: teamId },
      });

      devLog("Response from fetchPlayers:", response.data);
      const playersData = JSON.parse(response.data.body).message || [];

      // Ensure playersData is an array before calling map
      if (Array.isArray(playersData)) {
        const formattedPlayers = playersData.map((player) => ({
          player_name: player.player_name,
          PK: player.PK,
          SK: player.SK || `PLAYER#${player.player_name}`, // Ensure unique SK
          ...player, // Include other fields if needed
        }));

        devLog("Formatted players data:", formattedPlayers);
        setPlayers(formattedPlayers); // Update the players list with detailed data
        setError(""); // Clear any existing error
      } else {
        setPlayers([]); // Set an empty array if no players found
        setError("No players found for the selected team.");
      }
    } catch (error) {
      console.error("Error fetching detailed players:", error);
      setError("Failed to load players. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTeamChange = async (e) => {
    const selectedTeamId = e.target.value;
    const selectedTeamName = e.target.options[e.target.selectedIndex].text;

    devLog("Selected team ID:", selectedTeamId);
    devLog("Selected team name:", selectedTeamName);

    setSelectedTeamId(selectedTeamId); // Store selected team ID
    setSelectedTeamName(selectedTeamName); // Store selected team name

    // Fetch players for the selected team
    fetchPlayers(selectedTeamId);
  };
  const handleNavigateToManageTeam = () => {
    navigate("/manage-team");
  };
  // Handle adding a player manually (local, no DB update)
  const addPlayer = () => {
    if (playerInput.trim() !== "") {
      const newPlayer = {
        player_name: playerInput.trim(),
        SK: `TEMP#${uuidv4()}`, // Generate a temporary unique SK for the new player
      };
      setPlayers([...players, newPlayer]); // Add the new player to the list
      setPlayerInput(""); // Clear input field
    }
  };

  const removePlayer = (playerSK) => {
    const updatedPlayers = players.filter((player) => player.SK !== playerSK);
    setPlayers(updatedPlayers);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Sanitize folder name components
      const sanitizedDate = new Date().toISOString().replace(/:/g, "-");
      const sanitizedMyTeam = sanitizeFolderName(myTeam); // Use sanitized team name
      const sanitizedOpposition = sanitizeFolderName(opposition);
      const matchFolderName = `${sanitizedMyTeam}_${homeAway}_${sanitizedOpposition}_${sanitizedDate}`;

      const userFolder = userId; // Using userId as the user folder name
      const fullPath = `${userFolder}/${matchFolderName}`;

      // Correct the players array to output as an array of player names
      const formattedPlayers = players.map((player) => ({
        player_id: player.SK || uuidv4(), // Use existing ID or generate a temp UUID
        name: player.player_name,
        goals: 0,
        assists: 0,
        shots_on_target: 0,
        shots_off_target: 0,
        defensive_actions: 0,
        saves: 0,
      }));

      // Create matchInfo similar to the desired output
      const matchInfo = {
        myTeam, // Use the team name for display purposes
        opposition, // Opposition team name
        homeAway, // Home or Away status
        players: formattedPlayers, // Pass only player names as objects with stats
        otherInfo, // Any other additional match information
        matchId: `match-${uuidv4()}`, // Unique match ID
        match_folder: matchFolderName,
        match_date: new Date().toISOString(), // Current date as match date
        status: "ongoing", // Initial status
        teamScores: {
          // Add initial scores here
          myTeamScore: 0,
          oppositionScore: 0,
        },
      };

      devLog("Formatted match info for saving:", matchInfo);

      // Save match info to S3 (or other storage)
      await saveMatchInfoToS3(fullPath, matchInfo);
      devLog("Match info saved to S3");

      // Log the API payload being sent
      const apiPayload = {
        operationType: "update_current_match",
        data: {
          coach_id: userId,
          team_id: selectedTeamId,
          current_match: matchInfo,
        },
      };
      devLog("API Payload for updating match:", apiPayload);

      // API call to update the current match in the DynamoDB Coaches_table
      const response = await axios.post(`${API_URL}`, apiPayload);

      // Log the response details
      devLog("API Response status:", response.status);
      devLog("API Response data:", response.data);
      devLog("API Response headers:", response.headers);

      if (response.status === 200) {
        devLog("Match successfully updated for the coach in DynamoDB.");
      } else {
        console.error(
          "Failed to update the current match in DynamoDB:",
          response.statusText
        );
        devLog("Full response object:", response);
        setError("Error updating match details in DynamoDB.");
      }
      devLog("onSetupComplete parameters:", {
        matchFolderName: fullPath,
        players: formattedPlayers,
        myTeam,
        opposition,
        userId,
        selectedTeamId,
        matchId: matchInfo.matchId,
        match_folder: matchInfo.match_folder,
        match_date: matchInfo.match_date,
        status: matchInfo.status,
        otherInfo: matchInfo.otherInfo,
        homeAway,
      });

      onSetupComplete({
        matchFolderName: fullPath,
        players: formattedPlayers,
        myTeam,
        opposition,
        userId,
        selectedTeamId,
        matchId: matchInfo.matchId,
        matchFolder: matchInfo.match_folder,
        matchDate: matchInfo.match_date,
        status: matchInfo.status,
        otherInfo: matchInfo.otherInfo,
        homeAway,
        teamScores: matchInfo.teamScores, // Pass initial scores
      });

      // Programmatically navigate to PressToTalk page
      sessionStorage.setItem("accessedViaNavigation", "true");
      navigate("/press-to-talk");
    } catch (error) {
      console.error("Error handling match setup:", error);
      devLog(
        "Error details:",
        error.response ? error.response.data : error.message
      );
      setError(
        "An error occurred while setting up the match. Please try again."
      );
    }
  };

  const handleNavigateToMatchLengthAndFormation = () => {
    if (!opposition.trim()) {
      // Check if opposition is empty
      alert("Please enter the opposition team name before proceeding.");
      return;
    }
    const sanitizedDate = new Date().toISOString().replace(/:/g, "-");
    const sanitizedMyTeam = sanitizeFolderName(myTeam); // Use sanitized team name
    const sanitizedOpposition = sanitizeFolderName(opposition);
    const userFolder = `${userId}`;
    const matchFolderName = `${userFolder}/${sanitizedMyTeam}_${homeAway}_${sanitizedOpposition}_${sanitizedDate}`;
    const matchDate = new Date().toISOString();
    const matchId = `match-${uuidv4()}`; // Generate a new match ID

    const matchSetupData = {
      myTeam,
      opposition,
      players,
      homeAway,
      otherInfo,
      selectedTeamId,
      selectedTeamName,
      matchId,
      userId,
      matchFolderName,
      matchDate,
      initialTeamScores: { myTeamScore: 0, oppositionScore: 0 },
    };

    navigate("/match-length-formation", { state: matchSetupData });
  };

  // Save match info as JSON file to S3 (utility function)
  const saveMatchInfoToS3 = async (folderName, data) => {
    const fileName = `${folderName}/match-info.json`;
    const url = `https://${config.BucketName}.s3.${config.region}.amazonaws.com/${fileName}`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Error saving match info to S3");
      }
      devLog("Match info saved to S3");
    } catch (error) {
      console.error("Error uploading match info:", error);
    }
  };

  // Function to sanitize folder names (preserving case)
  const sanitizeFolderName = (name) => {
    return name
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_]/g, "")
      .replace(/^_+|_+$/g, "");
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white shadow-lg rounded-lg">
      <p> </p>
      <p> </p>

      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-bar">
          {/* Step 1 */}
          <div className={`progress-step ${true ? "active" : ""}`}>
            <div className="circle">1</div>
            <span className="label">Match Setup</span>
          </div>

          {/* Line */}
          <div className="progress-line"></div>

          {/* Step 2 */}
          <div className={`progress-step ${false ? "active" : ""}`}>
            <div className="circle">2</div>
            <span className="label">Team Selection</span>
          </div>

          {/* Line */}
          <div className="progress-line"></div>

          {/* Step 3 */}
          <div className={`progress-step ${false ? "active" : ""}`}>
            <div className="circle">3</div>
            <span className="label">Start Game</span>
          </div>
        </div>
      </div>

      <h1 className="text-3xl font-bold text-center mb-6">Match Setup</h1>

      {/* <form onSubmit={handleSubmit}> */}
      <form onSubmit={handleNavigateToMatchLengthAndFormation}>
        {/* Team Dropdown */}
        {teams.length > 1 && (
          <div className="mb-4">
            <label
              htmlFor="teams"
              className="block text-lg font-medium text-gray-700 mb-2"
            >
              Select Team
            </label>
            <select
              id="teams"
              value={selectedTeamId} // Match the selected team ID
              onChange={handleTeamChange}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select a Team</option>
              {teams.map((team) => (
                <option key={team.team_id} value={team.team_id}>
                  {team.team_name || team.team_id}{" "}
                  {/* Display team name or fallback to ID */}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Opposition Input */}
        <div className="mb-4">
          <div className="opposition-name-label-center">
            <label
              htmlFor="opposition"
              className="block text-lg font-medium text-gray-700 mb-2 opposition-name-label-center"
            ></label>
          </div>
          <input
            type="text"
            id="opposition"
            value={opposition}
            onChange={(e) => setOpposition(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Opposition Team Name"
            required
          />
        </div>

        {/* Home/Away Radio Button Toggle */}
        <div className="mb-4 switch-home-away">
          <label className="block text-lg font-medium text-gray-700 mb-2"></label>
          <div className="switch-home-away-container">
            <span className="switch-home-away-label">Home</span>
            <label className="switch-home-away-toggle">
              <input
                type="checkbox"
                checked={
                  homeAway === "away"
                } /* Toggles between Home and Away */
                onChange={(e) =>
                  setHomeAway(e.target.checked ? "away" : "home")
                }
              />
              <span className="slider round"></span>
            </label>
            <span className="switch-home-away-label">Away</span>
          </div>
        </div>

        {/* Players Display */}
        {/* {selectedTeamId && (
          <div className="mb-6">
            <label className="block text-lg font-medium text-gray-700 mb-2"></label>
            <div className="button-container">
              <button
                type="button"
                className="start-match-button"
                onClick={() => setPlayersVisible(!playersVisible)}
              >
                {playersVisible ? "Hide Players" : "Show Players"}
              </button>
            </div>
            {playersVisible && ( // Conditionally render the players list
              <ul className="space-y-2">
                {players.map((player) => (
                  <li
                    key={player.SK || player.PK} // Use a unique identifier if available
                    className="flex justify-between items-center bg-gray-100 px-4 py-2 rounded-md"
                  >
                    {player.player_name}
                    <button
                      type="button"
                      onClick={() => removePlayer(player.SK)}
                      className="remove-player-button"
                    >
                      <i className="fas fa-times" aria-hidden="true"></i>{" "}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )} */}

        {/* Other Info */}
        <div className="mb-6">
          <label
            htmlFor="otherInfo"
            className="block text-lg font-medium text-gray-700 mb-2"
          >
            Other Information
          </label>
          <textarea
            id="otherInfo"
            value={otherInfo}
            onChange={(e) => setOtherInfo(e.target.value)}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Any information that can help the AI compile the report"
            rows="4"
          ></textarea>
        </div>

        {/* Submit Button */}
        <div className="text-center">
          <button type="submit" className="start-match-button">
            Continue
          </button>
        </div>
        <div className="text-center">
          {/* <button
            type="button"
            className="start-match-button"
            onClick={handleNavigateToMatchLengthAndFormation}
          >
            Continue to Match Length and Formation
          </button> */}
        </div>
      </form>
    </div>
  );
};

export default MatchSetup;
